import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useLocation } from "react-router-dom";
import { MultiStepLoader as Loader } from "../../components/Loader/multi-step-loader";
import {
  markAsDoshaQuizCompleted,
  generateDoshaQuizAsyncResult,
} from "./index";
import {
  trackPageView,
  trackEngagement,
  trackConversion,
} from "../../utils/gtm";
import {
  captureUtmAttribution,
  captureLocationAsync,
  getUtmAttributionFromSources,
} from "../../utils/utmAttribution";
import {
  MINI_QUIZ_COMPLETED_KEY,
  MINI_QUIZ_NAME,
  MINI_QUIZ_PATH,
  DEFAULT_MINI_QUIZ_LANGUAGE,
  getFullDoshaAssessmentUrl,
  DOSHA_QUIZ_FAVICON_URL,
  getMiniQuizLocale,
} from "./miniQuizConfig";
import { useDoshaQuizFavicon } from "./useDoshaQuizFavicon";
import "./DoshaResult.css";
import "./MiniDoshaQuiz.css";

const MiniDoshaResult = () => {
  useDoshaQuizFavicon();

  const location = useLocation();
  const { finalData, userInfo, utmAttribution: routeUtmAttribution } =
    location.state || {};
  const utmAttribution = getUtmAttributionFromSources(
    routeUtmAttribution,
    finalData?.additional_data
  );
  const { token, playerId, dominantDosha, name, language } = userInfo || {};
  const [loading, setLoading] = useState(true);
  const streamDataRef = useRef({});
  const markedCompleteRef = useRef(false);

  const locale = useMemo(
    () => getMiniQuizLocale(language || DEFAULT_MINI_QUIZ_LANGUAGE),
    [language]
  );
  const result = locale.results[dominantDosha] || locale.results.vata;
  const ui = locale.ui;
  const fullAssessmentUrl = getFullDoshaAssessmentUrl(language || DEFAULT_MINI_QUIZ_LANGUAGE);

  useEffect(() => {
    if (!finalData || !userInfo) {
      return undefined;
    }

    const abortController = new AbortController();

    captureUtmAttribution();
    captureLocationAsync();
    trackPageView("Dosha Result", "/dosha-quiz/mini-result");
    trackEngagement("dosha_result_viewed", {
      dominant_dosha: dominantDosha,
      player_id: playerId,
    });
    trackConversion("dosha_quiz_result_viewed", null);

    const submissionData = {
      ...finalData,
      additional_data: {
        ...finalData.additional_data,
        attribution: utmAttribution,
      },
    };

    const finishApis = async (resultPayload = {}) => {
      if (markedCompleteRef.current) return;
      markedCompleteRef.current = true;

      try {
        if (!localStorage.getItem(MINI_QUIZ_COMPLETED_KEY)) {
          await markAsDoshaQuizCompleted(
            token,
            resultPayload,
            playerId,
            utmAttribution,
            MINI_QUIZ_NAME
          );
          localStorage.setItem(MINI_QUIZ_COMPLETED_KEY, "true");
        }
      } catch (error) {
        console.error("Error marking mini quiz as completed:", error?.message || error);
      }

      setLoading(false);
    };

    generateDoshaQuizAsyncResult(submissionData, token, {
      signal: abortController.signal,
    })
      .then((data) => {
        if (data && typeof data === "object") {
          streamDataRef.current = data;
        }
        finishApis(typeof data === "object" ? data : streamDataRef.current);
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("Error submitting mini quiz result:", error?.message || error);
        finishApis(streamDataRef.current);
      });

    return () => abortController.abort();
  }, []);

  if (!userInfo || !finalData) {
    return <Navigate to={MINI_QUIZ_PATH} replace />;
  }

  return (
    <>
      <Helmet>
        <title>{ui.pageTitle}</title>
        <meta name="description" content={ui.pageDescription} />
        <meta property="og:title" content={ui.pageTitle} />
        <meta property="og:description" content={ui.pageDescription} />
        <meta property="og:type" content="website" />
        <link rel="icon" href={DOSHA_QUIZ_FAVICON_URL} />
      </Helmet>
      <div className="wellness-container mini-dosha-result">
        <section className="mini-result-card">
          <h1>{ui.resultTitle}</h1>
          {name && <p className="mini-result-name">{ui.resultGreeting(name)}</p>}
          <div className="mini-result-badge">{result.headline}</div>
          <p className="mini-result-elements">{result.elements}</p>
          <p className="mini-result-description">{result.description}</p>
          <p className="mini-result-keywords">
            <strong>{result.keywordsLabel}</strong> {result.keywords.join(" • ")}
          </p>
          <div className="mini-result-cta">
            <p>{ui.ctaLead}</p>
            <a
              href={fullAssessmentUrl}
              className="submit-btn mini-result-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {ui.ctaButton}
            </a>
            <p className="mini-result-cta-note">{ui.ctaNote}</p>
          </div>
        </section>
        <Loader
          loadingStates={locale.loadingStates}
          loading={loading}
          duration={5000}
          startIndex={0}
          loopStartIndex={locale.loadingStates.length - 1}
        />
      </div>
    </>
  );
};

export default MiniDoshaResult;
